import { isRoadieApiError } from 'roadie-ui'
import * as Sentry from '@sentry/react'
import { getProfile } from './profile'
import { vehiclesApi } from 'util/server/vehiclesApi'
import { GetVehiclesResponseSchema, type GetVehiclesResponse } from 'data/vehicles'

// TODO: standarize the try catch pattern for all of these dispatch functions
export function getVehicles(profile_id: number) {
  return async (dispatch: any): Promise<Partial<GetVehiclesResponse>> => {
    try {
      const resp = await vehiclesApi.get(`/vehicles/profile/${profile_id}`)
      const validationResult = GetVehiclesResponseSchema.safeParse(resp.data)

      if (!validationResult.success) {
        Sentry.captureException(new Error('GET /vehicles/profile/:profile_id API response validation failed'), {
          level: 'warning',
          tags: {
            error_type: 'api_validation_failure',
            endpoint: '/vehicles/profile/:profile_id',
          },
          extra: {
            httpStatus: resp.rawResponse?.status,
            profileId: profile_id,
            validationIssues: JSON.stringify(validationResult.error.issues, null, 2),
            responseData: JSON.stringify(resp.data, null, 2),
          },
        })
      }

      const vehicles = resp.data
      dispatch({ type: 'SET_VEHICLES', vehicles })
      return vehicles
    } catch (error) {
      console.warn('Failed to fetch vehicles', error)

      // Fallback: empty array to not trigger error state
      dispatch({ type: 'SET_VEHICLES', vehicles: [] })
      return []
    }
  }
}

export function setActiveVehicle(profile_id: number, vehicle_id: number) {
  return (dispatch: any) => {
    return vehiclesApi.post(`/vehicles/active_vehicle`, { vehicle_id: vehicle_id }).then(async () => {
      return dispatch(getVehicles(profile_id))
    })
  }
}

export function createVehicle(data: Record<string, unknown>) {
  return vehiclesApi.post(`/vehicles/create`, data)
}

export function patchDecodePlate(profile_id: number, vehicle_id: number, plate: string, state?: string | null) {
  return async (dispatch: any) => {
    try {
      await vehiclesApi.patch(`/vehicles/${vehicle_id}/plate/decode?license_plate=${plate}&state=${state}`, {})

      const vehicles = await dispatch(getVehicles(profile_id))
      return {
        success: true,
        error: null,
        data: vehicles,
        status: vehicles.status,
      }
    } catch (err: any) {
      const status = err.response?.rawResponse.status || 500
      await dispatch(getVehicles(profile_id))
      return {
        success: false,
        error: err.message || 'Request failed',
        data: err.response?.data,
        status,
      }
    }
  }
}

export function patchAcceptRejectDecodedPlateVIN(
  profile_id: number,
  vehicle_id: number | undefined,
  action: 'accept' | 'reject'
) {
  return async (dispatch: any) => {
    try {
      await vehiclesApi.patch(`/vehicles/${vehicle_id}/plate/${action}`, {})
      const vehicles = await dispatch(getVehicles(profile_id))
      return {
        success: true,
        error: null,
        data: vehicles,
        status: vehicles.status,
      }
    } catch (err: any) {
      await dispatch(getVehicles(profile_id))
      return {
        success: false,
        error: err.message || 'Request failed',
        data: err.response?.data,
        status: err.response?.rawResponse.status || 500,
      }
    }
  }
}

export function resolveConflictingVIN(vin: string, vehicle_id: number) {
  return async (dispatch: any) => {
    try {
      const res = await vehiclesApi.patch(`/vehicles/${vehicle_id}/resolve_conflict?vin=${vin}`, {})
      await dispatch(getProfile())
      return {
        success: true,
        error: null,
        data: res.data,
        /** @ts-expect-error this code exists today and I'm unsure why roadie API doesn't type it right*/
        status: res.status,
      }
    } catch (err: any) {
      return {
        success: false,
        error: err.message || 'Request failed',
        data: err.response?.data,
        status: err.response?.rawResponse.status || 500,
      }
    }
  }
}

export function verifyInsuranceCard(file: any, profile_id: string, vehicle_id: number) {
  const data = new FormData()
  data.append('profile_id', profile_id)
  data.append('vehicle_id', String(vehicle_id))
  data.append('insurance_card_image', file)
  return vehiclesApi.post(`/vehicles/verify`, data)
}

export async function getInsurancePresignedUploadUrl(vehicle_id: number): Promise<string> {
  const response = await vehiclesApi.get(`/submissions/${vehicle_id}/presigned_upload`)
  const presignedUploadUrl = response.data?.url

  if (!presignedUploadUrl) {
    throw new Error('Missing presigned upload URL')
  }

  return presignedUploadUrl
}

export async function uploadInsuranceCardToPresignedUrl(url: string, file: any): Promise<Response> {
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'image/png',
    },
    body: file,
  })

  if (!response.ok) {
    throw new Error(`S3 upload failed with status ${response.status}`)
  }

  return response
}

export function verifyPresignedInsuranceCard(vehicle_id: number) {
  return vehiclesApi.patch(`/submissions/${vehicle_id}/verify_insurance`, {})
}

export async function getMostRecentSubmissionStatus(vehicle_id: number) {
  try {
    return await vehiclesApi.get(`/submissions/status`, { params: { vehicle_id } })
  } catch (err) {
    if (isRoadieApiError(err) && err.response?.rawResponse.status === 422) {
      return err.response
    }
    console.error('Error fetching insurance submission status:', err)
    throw err
  }
}

export function deleteVehicle(vehicle_id: number | undefined, profile_id: number) {
  return (dispatch: any) => {
    return vehiclesApi.delete(`/vehicles/${vehicle_id}`).then(() => {
      return dispatch(getVehicles(profile_id))
    })
  }
}
